/**
 * Inicio — MOBILE.
 *
 * Orden de la pantalla. §5.3 exige que el número grande de Inicio sea la calidad de la pérdida y
 * no el peso, y eso se respeta: es el único `cifra-heroe` de la pantalla y va arriba. Lo que
 * cambió es que ARRIBA DE ÉL va la franja de hoy — que no lleva ninguna cifra, así que no compite
 * por el papel de número grande, sólo dice a qué se vino y abre la puerta.
 *
 * Y la calidad de la pérdida se muestra donde corresponde según si existe: con dato, arriba, como
 * pide la spec. Sin dato (necesita 3 mediciones de balanza, o sea todos los primeros días) baja al
 * final, junto al gráfico de peso, explicando qué le falta. Antes la app se presentaba con veinte
 * placas huecas: lo primero que veía un usuario nuevo era un vacío que todavía no podía llenar.
 *
 * El peso no aparece arriba: §10 prohíbe anclar la app al peso.
 */

import { useState, type ReactNode } from 'react';
import { AVISO_BIOIMPEDANCIA, UMBRAL_PRATIO_BAJO, formatearNumero, nombreDia } from '@mg/core';

import { useInicio } from '../features/useInicio.ts';
import { useRuta } from '../ui/router.tsx';
import {
  Aviso,
  Cifra,
  Icono,
  Insignia,
  Pila,
  PilaDePlan,
  Vacio,
  estadosDeRatio,
} from '../ui/componentes.tsx';
import { LineaConMedia } from '../ui/charts.tsx';
import { Pista } from '../ui/Pista.tsx';
import { PrimerosPasos } from '../ui/PrimerosPasos.tsx';

export function InicioMobile(): ReactNode {
  const v = useInicio();
  const { ir } = useRuta();

  if (v.datos.cargando) return <Vacio>Cargando…</Vacio>;

  const conCalidad = v.pratio.estado === 'ok' && v.pratio.calidad !== null;

  return (
    <>
      <PrimerosPasos datos={v.datos} />

      <BandaDeHoy v={v} />

      {conCalidad && <TarjetaCalidad v={v} />}

      <ParaMirar v={v} />

      {v.calendario && v.plan && (
        <section className="tarjeta">
          <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
            <span className="eyebrow">Plan</span>
            <button type="button" className="boton boton--chico boton--fantasma" onClick={() => ir('calendario')}>
              Ver calendario
            </button>
          </div>

          <PilaDePlan
            usadas={v.plan.usadas}
            pendientes={v.plan.restantes}
            enRiesgo={v.calendario.sesionesEnRiesgo}
            alto={26}
          />

          <div className="fila fila--entre" style={{ marginTop: 'var(--e3)' }}>
            <div>
              <Cifra valor={v.plan.restantes} unidad={v.plan.restantes === 1 ? 'sesión' : 'sesiones'} decimales={0} />
              <p className="nota--fina">de {v.plan.sesionesTotales}, hasta el {v.datos.planActivo?.fecha_vencimiento}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Cifra
                valor={v.calendario.colchon}
                unidad={Math.abs(v.calendario.colchon) === 1 ? 'día' : 'días'}
                decimales={0}
              />
              <p className="nota--fina">de colchón</p>
            </div>
          </div>

          {v.calendario.colchonCritico && (
            <p className="nota" style={{ marginTop: 'var(--e3)', color: 'var(--ambar)' }}>
              {v.calendario.avisos.find((a) => a.tipo === 'colchon_critico')?.texto}
            </p>
          )}
        </section>
      )}

      <section className="tarjeta">
        <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
          <span className="eyebrow">Esta semana</span>
          <span className="lectura nota--fina">{formatearNumero(v.volumenSemanaKg / 1000, 1)} t</span>
        </div>

        <div className="columna" style={{ gap: 'var(--e3)' }}>
          {v.semana.celdas.map((c) => (
            <div key={c.grupo}>
              <div className="fila fila--entre" style={{ marginBottom: 3 }}>
                <span style={{ fontSize: 13, textTransform: 'capitalize' }}>{c.grupo}</span>
                <span className="lectura nota--fina">
                  {formatearNumero(c.seriesEfectivas, 1)}
                  {c.objetivo ? ` / ${c.objetivo}` : ''}
                  {/* El rojo sólo cuando hay abandono de verdad. `abandonado` de core también es
                      true el primer día —nadie entrenó nada todavía—, y una insignia roja que
                      dice NUNCA en la primera pantalla que ve alguien es un reproche por algo
                      imposible de haber hecho (§10). Ahí la misma fila dice "todavía no", en
                      neutro: es un dato que falta, no una falta. */}
                  {c.abandonado && (
                    <>
                      {' '}
                      <Insignia tono={c.estimulo.alarma ? 'alerta' : 'neutro'}>{c.estimulo.texto}</Insignia>
                    </>
                  )}
                </span>
              </div>
              <Pila
                estados={estadosDeRatio(
                  c.objetivo ? c.seriesEfectivas / c.objetivo : Math.min(1, c.seriesEfectivas / 12),
                  12,
                  c.estimulo.alarma ? 'alerta' : 'llena',
                )}
                alto={10}
                etiqueta={`${c.grupo}: ${formatearNumero(c.seriesEfectivas, 1)} series efectivas`}
              />
            </div>
          ))}
        </div>
      </section>

      {!conCalidad && <TarjetaCalidad v={v} />}

      {v.composicion.peso.length > 0 && (
        <section className="tarjeta">
          <div className="fila fila--entre" style={{ marginBottom: 'var(--e3)' }}>
            <span className="eyebrow">Peso · media de 7 días</span>
            <button type="button" className="boton boton--chico boton--fantasma" onClick={() => ir('cuerpo')}>
              <Icono nombre="balanza" tamano={15} />
            </button>
          </div>
          <LineaConMedia puntos={v.composicion.peso} alto={120} unidad="kg" />
          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            {AVISO_BIOIMPEDANCIA}
          </p>
        </section>
      )}
    </>
  );
}

/**
 * Qué toca hoy y el botón para arrancarlo, arriba de todo.
 *
 * Antes esto era un botón suelto debajo del KPI, y el nombre del día de rutina viajaba como
 * subtítulo dentro del botón. Un botón no es el lugar donde alguien busca "¿qué me toca hoy?":
 * la pregunta se responde antes de decidir tocarlo.
 *
 * Sin cifras a propósito — §5.3 reserva el número grande de Inicio para la calidad de la pérdida.
 *
 * El botón sólo es ámbar y de 72 px cuando la acción es la del día. Si ya entrenaste, o si hoy no
 * toca, va en el botón normal: un botón que grita "entrená" cuando entrenar no es lo que sigue
 * enseña a desconfiar de la franja entera.
 */
function BandaDeHoy({ v }: { v: ReturnType<typeof useInicio> }): ReactNode {
  const { ir } = useRuta();
  const q = v.queTocaHoy;

  return (
    <section className="banda-hoy">
      <div>
        <span className="eyebrow">Hoy · {nombreDia(v.datos.hoy)}</span>
        <h2 className="titulo" style={{ marginTop: 2 }}>
          {q.titulo}
        </h2>
        <p className="nota" style={{ marginTop: 2 }}>
          {q.detalle}
        </p>
      </div>

      <button
        type="button"
        className={q.esPrimaria ? 'boton boton--principal boton--registrar' : 'boton'}
        onClick={() => ir('registro')}
      >
        {q.accion}
      </button>
    </section>
  );
}

/**
 * "Para mirar": las alertas y lo que dejó de registrarse.
 *
 * Las otras alertas se abren acá mismo. Antes ese botón llevaba a Progreso, que no muestra
 * alertas en ninguna variante: prometía verlas y dejaba al usuario en otra pantalla sin ellas.
 */
function ParaMirar({ v }: { v: ReturnType<typeof useInicio> }): ReactNode {
  const { ir } = useRuta();
  const [mostrarTodas, setMostrarTodas] = useState(false);

  if (v.alertas.length === 0 && v.diasSinAnotarComida === null) return null;

  const visibles = mostrarTodas ? v.alertas : v.alertas.slice(0, 3);
  const ocultas = v.alertas.length - visibles.length;

  return (
    <section className="columna" style={{ gap: 'var(--e2)' }}>
      <span className="eyebrow">Para mirar</span>

      {visibles.map((a) => (
        <Aviso key={a.clave} tono={a.severidad === 'importante' ? 'importante' : 'atencion'} titulo={a.titulo}>
          {a.detalle}
          {a.accion && (
            <>
              {' '}
              <span className="apagado">{a.accion}</span>
            </>
          )}
        </Aviso>
      ))}

      {ocultas > 0 && (
        <button
          type="button"
          className="boton boton--fantasma boton--chico"
          onClick={() => setMostrarTodas(true)}
        >
          Ver las otras {ocultas}
        </button>
      )}

      {/* Dato del registro, no un reproche (§10): la app no dice qué comer ni cuánto, sólo que
          hace días que no hay nada anotado y que los promedios se apoyan en menos días. */}
      {v.diasSinAnotarComida !== null && (
        <div className="fila fila--entre" style={{ gap: 'var(--e3)' }}>
          <p className="nota">
            Hace {v.diasSinAnotarComida} {v.diasSinAnotarComida === 1 ? 'día' : 'días'} que no anotás
            comida.
          </p>
          <button
            type="button"
            className="boton boton--chico"
            style={{ flex: 'none' }}
            onClick={() => ir('nutricion')}
          >
            Anotar
          </button>
        </div>
      )}
    </section>
  );
}

/**
 * La calidad de la pérdida. Una sola pieza para los dos estados: con dato es el héroe de §5.3, y
 * sin dato es una promesa explicada. Quien la usa decide DÓNDE ponerla según cuál de los dos sea
 * — arriba si hay número, al final si todavía no.
 */
function TarjetaCalidad({ v }: { v: ReturnType<typeof useInicio> }): ReactNode {
  const { ir } = useRuta();
  const { pratio } = v;

  const tono =
    pratio.estado !== 'ok' || pratio.calidad === null
      ? 'llena'
      : pratio.calidad >= UMBRAL_PRATIO_BAJO
        ? 'llena'
        : 'alerta';

  return (
    <section className="tarjeta" style={{ paddingTop: 'var(--e5)' }}>
      <span className="eyebrow">Calidad de la pérdida</span>

      {pratio.estado === 'ok' && pratio.calidad !== null ? (
        <>
          <div className="fila" style={{ alignItems: 'baseline', gap: 'var(--e2)', marginTop: 4 }}>
            <span className="cifra-heroe">{Math.round(pratio.calidad)}</span>
            <span className="unidad" style={{ fontSize: 22 }}>
              %
            </span>
            {pratio.recomposicion && <Insignia tono="agua">recomposición</Insignia>}
          </div>

          <div style={{ marginTop: 'var(--e3)' }}>
            <Pila
              estados={estadosDeRatio(pratio.calidad / 100, 20, tono)}
              alto={30}
              etiqueta={`${Math.round(pratio.calidad)} % de lo que bajaste fue grasa`}
            />
          </div>

          <p className="nota" style={{ marginTop: 'var(--e3)' }}>
            {pratio.detalle}
          </p>

          <div style={{ marginTop: 'var(--e3)' }}>
            <Pista clave="pila-pratio">
              Cada barrita es un 5 % de lo que bajaste. Las llenas son grasa; las huecas, masa magra
              que entregaste.
            </Pista>
          </div>

          {pratio.condicionesMixtas && (
            <p className="nota--fina" style={{ marginTop: 4, color: 'var(--ambar)' }}>
              Hay lecturas tomadas en condiciones distintas en esta ventana.
            </p>
          )}
        </>
      ) : (
        <>
          {/* Sin dato no va un guion gigante: se dibuja la pila vacía, que dice que la métrica
              existe y todavía no se puede llenar, en vez de parecer un error de carga. Más chica
              que la del héroe — acá el protagonista es lo que hay que hacer para llenarla. */}
          <p className="nota" style={{ marginTop: 'var(--e2)' }}>
            De todo el peso que bajes, cuánto fue grasa y cuánto músculo. Es el número que esta app
            existe para responder, y es el único que una balanza sola no te puede dar.
          </p>

          <div style={{ marginTop: 'var(--e4)' }}>
            <Pila estados={new Array(20).fill('hueca')} alto={16} etiqueta="Todavía sin calcular" />
          </div>

          <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
            {pratio.detalle}
          </p>

          <button
            type="button"
            className="boton boton--chico"
            style={{ marginTop: 'var(--e3)' }}
            onClick={() => ir('cuerpo')}
          >
            <Icono nombre="balanza" tamano={16} />
            Registrar una medición
          </button>
        </>
      )}

      {v.faltaAltura && (
        <div style={{ marginTop: 'var(--e4)' }}>
          {/* El destino va en un botón en su propia línea y no nombrado en el texto: un "andá a
              Ajustes" en medio de una frase obliga a buscarlo a mano y se lee peor en una línea. */}
          <Aviso tono="atencion" titulo="Falta tu altura">
            Sin altura no se puede calcular el FFMI.{' '}
            {v.alturaSugerida
              ? `Tu balanza reporta IMC, así que podemos estimarla en ${v.alturaSugerida} cm: falta que la confirmes.`
              : 'Se pide una sola vez.'}
            <div style={{ marginTop: 'var(--e3)' }}>
              <button type="button" className="boton boton--chico" onClick={() => ir('ajustes')}>
                Completar mi altura
              </button>
            </div>
          </Aviso>
        </div>
      )}
    </section>
  );
}
